import { Language } from '@/shared/model/Language';

/** `settings.notifications.firebaseTopic` — the topic admin pushes to, per notification language. */
export type FirebaseTopics = Partial<Record<Language, string>>;

type TopicBridge = Pick<NonNullable<Window['OrderMagePlugin']>, 'subscribeTopic' | 'unsubscribeTopic'>;

const TOPIC_PATH_PREFIX = '/topics/';

/**
 * Admin stores the topic as the FCM send path (`/topics/<name>`), but the native SDK takes the
 * bare name and throws on any topic containing `/`.
 */
function normalizeTopic(topic: string | undefined): string | null {
    const trimmed = topic?.trim() ?? '';
    const name = trimmed.startsWith(TOPIC_PATH_PREFIX) ? trimmed.slice(TOPIC_PATH_PREFIX.length) : trimmed;

    return name || null;
}

export function resolveFirebaseTopics(
    topics: FirebaseTopics | null | undefined,
    lang: Language,
): { subscribe: string | null; unsubscribe: string[] } {
    const subscribe = normalizeTopic(topics?.[lang]);
    const others = Object.values(topics ?? {})
        .map(normalizeTopic)
        .filter((topic): topic is string => topic !== null && topic !== subscribe);

    return { subscribe, unsubscribe: [...new Set(others)] };
}

/**
 * Admin sends a topic-configured notification to the topic only, never to device tokens, so a
 * device that has not subscribed receives nothing. The native app subscribes solely on request
 * from the web, which makes this call the only thing that enrols the device.
 */
export async function syncFirebaseTopics(
    plugin: TopicBridge,
    topics: FirebaseTopics | null | undefined,
    lang: Language,
): Promise<void> {
    const { subscribe, unsubscribe } = resolveFirebaseTopics(topics, lang);

    // The bridge rejects on its own timeout; a failed call must not break the page.
    await Promise.allSettled([
        ...(subscribe ? [plugin.subscribeTopic(subscribe)] : []),
        ...unsubscribe.map((topic) => plugin.unsubscribeTopic(topic)),
    ]);
}
